import React, { FC, useContext, useEffect, useMemo } from 'react';
import { Modal, Spin } from 'antd';
import { TPODataContext } from 'contexts/TPOContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import axios from 'axios';
import { queryClient } from 'services/reactQueryService';
import { antIcon } from 'components/pages/Login';
import PromoDetails from '../AddEventModal/Promos';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { Metric, ProductPPG, PromoConfigDictionary, PromoTactic } from 'types/tradePromoOptimization';
import { formatDatePreservingTimeValues, getConfig, sendNotification } from 'utils/helperMethods';
import { NEW_EVENT_PROMO_ID } from 'config/constants';
import { useWindowSize } from 'usehooks-ts';
import _ from 'lodash';
import { manualOfferDropdownOptions } from '../AddEventModal/PromoSections/PromoInfoSection';
import { UserContext } from 'contexts/ConfigContext';
import { ConfigKeys, PromotionTypes, tpoPromoMetricKeys } from 'utils/Enums';
import { getPromoMetricByNameKeyName, getQuarter } from '../tpoHelperMethods';
import { TpoMessages } from 'constants/tpo';
import { validateTPOAdjustedBaseUnits } from 'utils/validators/tpoPromoValidators';

type Props = {
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
    eventId: null | string;
    eventPromoId: null | string;
    disabled: boolean;
};

const CustomModal = styled(Modal)`
    .ant-modal-body {
        padding: 0px 36px;
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    .ant-modal-content {
        border-radius: 8px !important;
    }
    .ant-modal-header {
        border-radius: 8px 8px 0px 0px !important;
        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
`;
const errorMessageText = 'Please enter the mandatory fields';
const AddOrEditPromoModal: FC<Props> = ({ open, onCancel, eventId, eventPromoId, disabled }) => {
    const {
        selectedEventInfo,
        updateSelectedEventProperty,
        setPromoActiveTab,
        isCurrentPromoFormDisabled,
        isValid,
        setTempEventProperties,
        errorMessage,
        setErrorMessage,
        promoConfigDictionary,
        selectedPlanInfo,
        promoActiveTab,
        clearPromoPopupErrors,
        setPromoPopupErrors,
        promoPopupErrors,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const {
        units: { currency, dateFormat },
    } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const modalButtonTitle = 'Save';
    const { width, height } = useWindowSize();
    const percentageHeight = height * 0.9; // to keep save button above help button
    const percentageWidth = width * 0.95;

    //reset all fields to default when popup closes
    useEffect(() => {
        if (!open) {
            //clear cache
            queryClient.removeQueries({
                exact: false,
                queryKey: ['tpo_promo_names'],
            });
        }

        return () => {
            queryClient.removeQueries({
                queryKey: ['tpo-event-data-by-id', { eventId }],
                exact: true,
            });

            clearPromoPopupErrors();
        };
    }, [open]);

    const mechanicType = useMemo(() => {
        // Default label value
        let label = '';

        // Ensure promoType exists before proceeding
        if (selectedEventInfo?.currentPromoTactic?.promoType) {
            // Determine the appropriate offer label based on the offer type
            const { offerType, manualOffer, offer, promoType, comboUnit, discountUnit, discount, buyGet, buyFor } =
                selectedEventInfo.currentPromoTactic;

            let offerLabel =
                offerType === 'manual'
                    ? ['combination_offer', 'buy_for'].includes(manualOffer || '')
                        ? ''
                        : manualOfferDropdownOptions.find((item) => item.value === manualOffer)?.label || ''
                    : offer;

            // Construct the mechanic type label using the promo type and offer label
            label = `${promoConfigDictionary.promotions[promoType]}_${offerLabel ?? ''}`;
            if ([PromotionTypes.Merchandising, PromotionTypes.SM_MDF].includes(promoType)) {
                if (manualOffer === 'price_off') {
                    if (discountUnit === '%') {
                        label += `_${discount ?? ''}${discountUnit}`;
                    } else {
                        label = label + `_${currency}${discount ?? ''}`;
                    }
                } else if (manualOffer === 'combination_offer') {
                    if (comboUnit === '%') {
                        label += `Buy_${buyGet?.[0] ?? 0}_Get_${buyGet?.[1] ?? 0}${comboUnit}_Off`;
                    } else if (comboUnit === 'abs') {
                        label += `Buy_${buyGet?.[0] ?? 0}_Get_${currency}${buyGet?.[1] ?? 0}_Off`;
                    } else {
                        label += `Buy_${buyGet?.[0] ?? 0}_Get_${buyGet?.[1] ?? 0}`;
                    }
                } else if (manualOffer === 'buy_for') {
                    label += `${buyFor?.[0]}_For_$${buyFor?.[1]}`;
                }
            } else {
                label += 'single'; // By default we will add _single for all the other promotions
            }
        }

        return label.toLocaleUpperCase();
    }, [
        selectedEventInfo?.currentPromoTactic?.promoType, // Recompute if promoType changes
        selectedEventInfo?.currentPromoTactic?.offerType, // Ensure changes in offerType are reflected
        selectedEventInfo?.currentPromoTactic?.offer, // Track changes in offer value
        selectedEventInfo?.currentPromoTactic?.manualOffer, // Track manual offer changes
        selectedEventInfo?.currentPromoTactic?.comboUnit,
        selectedEventInfo?.currentPromoTactic?.discount,
        selectedEventInfo?.currentPromoTactic?.discountUnit,
        selectedEventInfo?.currentPromoTactic?.buyGet,
        selectedEventInfo?.currentPromoTactic?.buyFor,
        promoConfigDictionary,
    ]);

    //get all promos for a particular event id
    const { loading: getAvailablePromoNamesLoading } = useCommonApiHandler({
        method: 'get',
        queryKey: ['tpo_promo_names', eventId],
        queryConfig: { enabled: open && !!eventId },
        url: `${tradePromoOptimizationUrls.getPromosByEventId}/${eventId}`,
        parserFunction: (res: any) => {
            try {
                if (res?.data?.length > 0) {
                    updateSelectedEventProperty(
                        'promoTacticsIdNamePair',
                        res?.data?.map((d: any) => ({ name: d.name, id: d.event_promo_id })),
                    );

                    if (res.data[0].event_promo_id) {
                        // set first id as active promo tab
                        setPromoActiveTab(eventPromoId ?? res.data[0].event_promo_id);
                    }
                } else {
                    updateSelectedEventProperty('promoTacticsIdNamePair', []);
                    setPromoActiveTab(null);
                }
                return {};
            } catch (error) {
                throw error;
            }
        },
    });

    //get event meta details to fill the form in edit flow
    const { loading: eventDataLoading, error: eventDataError } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: open && Boolean(eventId) && !!promoActiveTab, cacheTime: 0, staleTime: 0 },
        queryKey: [`tpo-event-data-by-id`, { eventId, promoActiveTab }],
        url: `${tradePromoOptimizationUrls.getEventDataByIdUrl}/${eventId}`,
        parserFunction: (res: any) => {
            const startDateFormatted = res?.start_date ? formatDatePreservingTimeValues(res?.start_date) : null;
            const endDateFormatted = res?.end_date ? formatDatePreservingTimeValues(res?.end_date) : null;

            const tempState = {
                name: res?.event_name,
                eventId: res?.event_id,
                selectedDim1NestedAttributes: res?.selected_attribute_nested?.map((item: any) => ({
                    id: item?.dim_id,
                    value: item?.value,
                })),
                CRAStartDate: startDateFormatted,
                CRAEndDate: endDateFormatted,
                selectedAttributes: res.selected_attribute,
            };
            setTempEventProperties(tempState);
            return {};
        },
    });

    // Save promo details
    const savePromoDetails = async (promo: PromoTactic): Promise<void> => {
        const updatedPPGs = promo.ppgs
            .filter((p: ProductPPG) => p.isSelected)
            .map((p: ProductPPG) => {
                return {
                    lowest_dim_attr_id: p.id,
                    lowest_dim_attr_name: p.name,
                    is_selected: p.isSelected,
                    promo_metrics: p.metrics.map((m: Metric) => ({
                        key: m.id,
                        input_value: Number(m.value),
                        recom_value: Number(m.recomValue),
                        is_selected: m.isSelected ?? false, // Default to false if null/undefined
                    })),
                    promo_spend_details: p.spendDetails.map((m: Metric) => ({
                        key: m.id,
                        input_value: Number(m.value),
                        recom_value: Number(m.recomValue),
                        is_selected: m.isSelected ?? false, // Default to false if null/undefined
                    })),
                };
            });

        const promoData = {
            plan_id: selectedPlanInfo?.planId,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            event_id: eventId,
            promo_id: promo?.promoId === '0' ? null : promo?.promoId,
            name: promo.name,
            tpm_promo_id: promo.tpmPromoId,
            new_status: promo.status || '',
            custom_promo_grouping: promo.customPromo || '',
            promo_type: promo.promoType,
            tactic_type: promo.tacticType,
            tactic_feature_value: promo.featureQuality,
            tactic_display_value: promo.displayQuality,
            promo_start_date: promo.promoStartDate,
            promo_end_date: promo.promoEndDate,
            cra_start_date: promo.craStartDate,
            cra_end_date: promo.craEndDate,
            offer_type: promo.offerType,
            offer_details: {
                offer_mechanic: promo.offer,
                manual_details: {
                    type: promo.manualOffer,
                    value: promo.discount,
                    buy: promo?.buyGet?.[0] || null,
                    get: promo?.buyGet?.[1] || null,
                    is_value_percentage: promo.discountUnit === '%',
                    combo_unit: promo?.buyGet?.length ? promo.comboUnit : null,
                    bf_buy: promo?.buyFor?.[0] || null,
                    bf_for: promo?.buyFor?.[1] || null,
                },
            },
            promo_mechanic: mechanicType,
            ppgs: updatedPPGs,
            last_updated_at: promo?.lastUpdatedAt || Date.now(),
            par: promo.parNumber,
            contract: promo.contractNumber,
            funding_request: promo.fundingRequest,
            ism_campaign_id: promo.ismCampaignId,
        };

        const response = await axios.post(tradePromoOptimizationUrls.postPromoDetails, promoData);
        return response.data;
    };

    // React Query: Save promo details
    const savePromoMutation = useMutation<void, Error, PromoTactic>({
        mutationFn: savePromoDetails,
        onSuccess: (data: any) => {
            sendNotification('success', 'Promo details saved successfully!');
            if (selectedEventInfo?.currentPromoTactic) {
                const newPromoTactic: PromoTactic = { ...selectedEventInfo.currentPromoTactic, hasChanges: false };
                updateSelectedEventProperty('currentPromoTactic', newPromoTactic);

                const promoTacticsIdNamePair = selectedEventInfo.promoTacticsIdNamePair || [];

                const updatedIdPair = promoTacticsIdNamePair.map((item) => {
                    // Check if the current item is a newly created promo
                    if (item.id === NEW_EVENT_PROMO_ID) {
                        // update active tab
                        setPromoActiveTab(data.event_promo_id);
                        return { id: data.event_promo_id, name: data.name };
                    }
                    // Update the name if the promo ID matches
                    if (item.id === data.event_promo_id) {
                        return { ...item, name: data.name };
                    }
                    return item;
                });

                if (data.event_promo_id === selectedEventInfo.firstPromoTacticBackup?.promoId) {
                    updateSelectedEventProperty('firstPromoTacticBackup', {
                        ...selectedEventInfo.firstPromoTacticBackup,
                        promoInfo: _.cloneDeep(newPromoTactic),
                    });
                }

                updateSelectedEventProperty('promoTacticsIdNamePair', updatedIdPair);
            }
            queryClient.removeQueries({
                exact: false,
                queryKey: ['tpo_get_promo_details_by_id'],
            });

            // todo temp changes- make changes to get the data from response and update it
        },
        onError: (err: any) => {
            setPromoPopupErrors(err?.response?.data?.detail?.errors ?? []);

            if (err?.response?.data?.detail?.errors?.length > 0) {
                sendNotification(
                    'error',
                    <span>
                        We encountered some errors. Please click on
                        <span className="text-redAlt mx-1">Error(s)</span>
                        link to view the error details.
                    </span>,
                    50,
                );
            } else {
                sendNotification('error', 'Failed to save promo details.');
            }
        },
    });

    const validateForm = () => {
        if (selectedEventInfo?.currentPromoTactic?.isRefreshRequired) {
            sendNotification('error', 'Please refresh to see the updated data');
            return false;
        }
        // If list price is selected it should be greater than zero
        if (
            hasInvalidSelectedMetric(
                getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.LIST_PRICE)?.name,
                0,
            ) ||
            hasInvalidSelectedMetric(
                getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.SRP)?.name,
                0,
            ) ||
            hasInvalidPromoSRPValue(
                getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.PROMOTED_SRP)?.name,
                0,
            ) ||
            validateEdlpQuarterSpan() ||
            validatePromoSpendErrorsSequentially() ||
            validateIfNoSpendsForMerchType() ||
            validateTPOAdjustedBaseUnits(selectedEventInfo, promoConfigDictionary)
        ) {
            return false;
        }

        if (selectedEventInfo?.currentPromoTactic?.promoType === PromotionTypes.EDLP) {
            return validatePromoTypeEDLP();
        } else {
            return validateNonEDLPPromoType();
        }
    };

    function validateEdlpQuarterSpan(): boolean {
        const warnings: string[] = [];
        const currentPromo = selectedEventInfo?.currentPromoTactic;

        const miscFixedMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.FIXED_MISC);
        const scanVolumeMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.SCAN_VOLUME);

        const promoStart = currentPromo?.promoStartDate;
        const promoEnd = currentPromo?.promoEndDate;

        if (!promoStart || !promoEnd) return true; // Skip if dates are not selected

        const startQuarter = getQuarter(promoStart);
        const endQuarter = getQuarter(promoEnd);

        currentPromo?.ppgs?.forEach((ppg) => {
            const miscFixed = Number(ppg.spendDetails.find((m) => m.id === miscFixedMetric.id)?.value ?? 0);
            const scanRate = Number(ppg.spendDetails.find((m) => m.id === scanVolumeMetric.id)?.value ?? 0);

            if ((miscFixed > 0 || scanRate > 0) && startQuarter !== endQuarter) {
                warnings.push(ppg.name ?? 'Unnamed PPG');
            }
        });

        if (warnings.length > 0) {
            sendNotification(
                'error',
                TpoMessages.error.EDLP_PPG_QUARTER_SPAN(miscFixedMetric.name, scanVolumeMetric.name, warnings),
            );
            return true;
        }

        return false;
    }

    const hasInvalidSelectedMetric = (metricName: string, invalidValue: number) => {
        if (
            selectedEventInfo?.currentPromoTactic.ppgs.some((product) => {
                return (
                    product.isSelected &&
                    product.metrics.some((metric) => {
                        return metric.name === metricName && Number(metric.value) === invalidValue;
                    })
                );
            })
        ) {
            sendNotification('error', TpoMessages.error.INVALID_VALUE(metricName, invalidValue));
            return true;
        }
        return false;
    };

    const hasInvalidPromoSRPValue = (metricName: string, invalidValue: number) => {
        if (
            selectedEventInfo?.currentPromoTactic.ppgs.some((product) => {
                return (
                    product.isSelected &&
                    product.metrics.some((metric) => {
                        return metric.name === metricName && Number(metric.value) === invalidValue;
                    })
                );
            })
        ) {
            sendNotification('error', TpoMessages.error.INVALID_VALUE(metricName, invalidValue));
            return true;
        }
        return false;
    };

    const validatePromoTypeEDLP = () => {
        if (
            selectedEventInfo?.currentPromoTactic?.craStartDate !==
                selectedEventInfo?.currentPromoTactic?.promoStartDate ||
            selectedEventInfo?.currentPromoTactic?.craEndDate !== selectedEventInfo?.currentPromoTactic?.promoEndDate
        ) {
            sendNotification('error', TpoMessages.error.EDLP_PROMO_CRA_DATE);
            return false;
        }
        return true;
    };

    const validateNonEDLPPromoType = (): boolean => {
        const edlpMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.EDLP_SRP);
        const promotedSRPMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.PROMOTED_SRP);

        const violations: string[] = [];

        selectedEventInfo?.currentPromoTactic?.ppgs
            ?.filter((ppg: ProductPPG) => ppg.isSelected)
            .forEach((ppg) => {
                const edlpValue = Number(ppg.metrics.find((m) => m.id === edlpMetric?.id)?.value ?? 0);
                const promotedValue = Number(ppg.metrics.find((m) => m.id === promotedSRPMetric?.id)?.value ?? 0);

                if (promotedValue > edlpValue) {
                    violations.push(ppg.name);
                }
            });

        if (violations.length > 0) {
            sendNotification('error', TpoMessages.error.NON_EDLP_PROMOTED_SRP_EXCEEDS(violations));
            return false;
        }

        return true;
    };

    const validatePromoSpendErrorsSequentially = (): boolean => {
        if (!selectedEventInfo?.currentPromoTactic) return false;

        const spendKeysToCheck = [
            tpoPromoMetricKeys.OI_RATE,
            tpoPromoMetricKeys.BILL_BACK_PERCENT,
            tpoPromoMetricKeys.BILL_BACK_RATE,
            tpoPromoMetricKeys.OI_PERCENT,
            tpoPromoMetricKeys.NBOI_RATE,
        ];

        for (const key of spendKeysToCheck) {
            const errorMessage = getZeroValueMetricError(promoConfigDictionary, selectedEventInfo, key);
            if (errorMessage) {
                sendNotification('error', errorMessage);
                return true; // error found, stop further checks
            }
        }

        return false; // no error
    };

    const validateIfNoSpendsForMerchType = (): boolean => {
        if (selectedEventInfo?.currentPromoTactic.promoType !== PromotionTypes.Merchandising) return false;

        const isSpendsFound = selectedEventInfo.currentPromoTactic.ppgs[0]?.spendDetails?.some((m) => m.isSelected);

        if (!isSpendsFound) {
            sendNotification('error', TpoMessages.error.MERCH_SHOULD_HAVE_MINIMUM_ONE_SPEND);
            return true;
        }

        return false; // no error
    };

    const getZeroValueMetricError = (
        promoConfigDictionary: PromoConfigDictionary,
        selectedEventInfo: any,
        metricKey: tpoPromoMetricKeys,
    ): string | null => {
        const metricInfo = getPromoMetricByNameKeyName(promoConfigDictionary, metricKey);

        if (!metricInfo) return null;

        const selectedPPGs =
            selectedEventInfo?.currentPromoTactic?.ppgs?.filter((ppg: ProductPPG) => ppg.isSelected) || [];
        if (selectedPPGs.length === 0) return null;

        const firstPPG = selectedPPGs[0];

        // If the first selected PPG does not have this metric selected, skip validation
        const isMetricSelected = firstPPG.spendDetails.some((sd: Metric) => sd.id === metricInfo.id && sd.isSelected);
        if (!isMetricSelected) return null;

        // Check if all selected PPGs have zero value for the metric
        const allZero = selectedPPGs.every((ppg: ProductPPG) => {
            const value = Number(ppg.spendDetails.find((sd) => sd.id === metricInfo.id)?.value ?? 0);
            return value === 0;
        });

        return allZero ? `${metricInfo.name} cannot be zero for all items.` : null;
    };

    const triggerWarningsFunction = () => {
        if (selectedEventInfo?.currentPromoTactic?.promoType === PromotionTypes.Merchandising) {
            const warnings: string[] = [];
            const listPriceMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.LIST_PRICE);
            const oiMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.OI_RATE);
            const nboiMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.NBOI_RATE);

            const billBackMetric = getPromoMetricByNameKeyName(
                promoConfigDictionary,
                tpoPromoMetricKeys.BILL_BACK_RATE,
            );
            const percentMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.OI_PERCENT);
            const scanMetric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.SCAN_RATE);

            selectedEventInfo.currentPromoTactic.ppgs
                .filter((ppg: ProductPPG) => ppg.isSelected)
                .forEach((ppg) => {
                    // promo keys
                    const listPrice = Number(ppg.metrics.find((m) => m.id === listPriceMetric.id)?.value ?? 0);
                    // spend keys
                    const oi = Number(ppg.spendDetails.find((m) => m.id === oiMetric.id)?.value ?? 0);
                    const nboi = Number(ppg.spendDetails.find((m) => m.id === nboiMetric.id)?.value ?? 0);
                    const billBack = Number(ppg.spendDetails.find((m) => m.id === billBackMetric.id)?.value ?? 0);
                    const percent = Number(ppg.spendDetails.find((m) => m.id === percentMetric.id)?.value ?? 0);
                    const scan = Number(ppg.spendDetails.find((m) => m.id === scanMetric.id)?.value ?? 0);
                    const totalDiscount = oi + nboi + billBack + (percent * listPrice) / 100 + scan;
                    const netPrice = listPrice - totalDiscount;

                    if (listPrice > 0 && netPrice < 0.5 * listPrice) {
                        warnings.push(`${ppg.name ?? 'Unnamed PPG'} drops below 50% of list price.`);
                    }
                });

            if (warnings.length > 0) {
                sendNotification('info', TpoMessages.info.TRADE_RATE_EXCEEDS_50_INFO(warnings), 10);
            }
        }
    };

    const onSubmit = async () => {
        if (disabled || !isValid || savePromoMutation?.isPending) return;

        if (!validateForm()) return;

        // trigger warnings function
        triggerWarningsFunction();
        try {
            setPromoPopupErrors([]);
            await savePromoMutation.mutateAsync(selectedEventInfo?.currentPromoTactic!);
        } catch {
            // Do not switch tabs if save fails
            // sendNotification('error', 'Failed to save promo details.');
        }
    };

    // Save promo and switch tabs
    const handlePromoTabChange = async (nextTab: number) => {
        if (!isValid) {
            setErrorMessage(errorMessageText);
            return;
        }
        if (disabled || !selectedEventInfo?.currentPromoTactic.hasChanges) {
            // If in view mode or no changes, switch tab directly
            setPromoActiveTab(nextTab);
            return;
        }

        if (!validateForm()) return;

        // If changes exist, save before switching
        try {
            if (selectedEventInfo) {
                sendNotification('info', TpoMessages.info.CHANGES_ARE_BEING_SAVED);
                await savePromoMutation.mutateAsync(selectedEventInfo.currentPromoTactic);
                setPromoActiveTab(nextTab); // Switch to the next tab
                updateSelectedEventProperty('currentPromoTactic', {
                    ...selectedEventInfo.currentPromoTactic,
                    hasChanges: false,
                });
                clearPromoPopupErrors();
            }
        } catch {
            // Do not switch tabs if save fails
            sendNotification('error', TpoMessages.error.FAILED_TO_SAVE);
        }
    };

    const getModalTitle = () => {
        return (
            <div className="flex flex justify-start gap-4">
                <span> {`${disabled ? 'View' : 'Add/Edit'} Promo`} </span>
            </div>
        );
    };
    const handleCancel = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (errorMessage) {
            setErrorMessage('');
        }
        if (savePromoMutation?.isPending) return;
        onCancel(e);
    };

    return (
        <div>
            <CustomModal
                style={{
                    borderRadius: 8,
                    height: `calc(${percentageHeight}px)`,
                }}
                bodyStyle={{ height: `calc(${percentageHeight}px - 106px)` }}
                width={percentageWidth}
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{
                    htmlType: 'submit',
                    disabled:
                        !isValid ||
                        !selectedEventInfo?.currentPromoTactic?.hasChanges ||
                        !selectedEventInfo.currentPromoTactic?.ppgs?.length,
                    style: {
                        borderRadius: 6,
                        display: disabled || isCurrentPromoFormDisabled ? 'none' : 'inline-block',
                    },
                    className: 'disabled:bg-red-500 disabled:opacity-50 ',
                }}
                okText={
                    savePromoMutation?.isPending ? (
                        <Spin indicator={antIcon} style={{ color: 'white' }} />
                    ) : (
                        modalButtonTitle
                    )
                }
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                onCancel={handleCancel}
                title={getModalTitle()}
            >
                {getAvailablePromoNamesLoading ? (
                    <div className="flex-1 border border-backgroundGreyAlt6  bg-backgroundGreyAlt6  rounded p-3   flex justify-center items-center  ">
                        <Spin />
                    </div>
                ) : (
                    <PromoDetails
                        onPromoActiveTabChange={handlePromoTabChange}
                        mechanicType={mechanicType}
                        eventDataLoading={eventDataLoading}
                        disabled={disabled}
                        eventId={eventId}
                    />
                )}
            </CustomModal>
        </div>
    );
};

export default AddOrEditPromoModal;
